import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'next-i18next'
import axios from 'axios'
import { type DeltaStatic } from 'quill'
import { getCookie } from 'cookies-next'
import { useSession } from 'next-auth/react'
import { useMountedState } from 'react-use'

import { solidBackgroundVideoApi } from '@/redux/api/tools/solidBackgroundVideoApi'
import {
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_MODEL_ID,
  DEFAULT_PROJECT_NAME,
  ORIENTATION,
  Orientation,
  SCRIPT_TYPE,
  ScriptType,
  SOLID_BACKGROUND_VIDEO_GENERATE_STATE,
  TextScriptTempo,
} from '@/constants/tools/solid-background-video'
import { useGetModelVoicesQuery } from '@/redux/api/modelApi'
import { Gender, GENDER } from '@/constants/gender'
import { useGetModelsQuery } from '@/redux/api/objectApi'
import {
  isMatchLanguage,
  isSameLanguage,
  isSimilarChineseLanguage,
} from '@/lib/language-code-utils'
import { useAppSelector } from '@/hooks/redux'
import { getDashBoardState } from '@/redux/store'

import { CustomMenuToolsLayout } from '../../common/components/ToolsLayout'
import { useToolsNotification } from '../../common/hooks/notification'

import { AudioPlayer } from './AudioPlayer'
import { CanvasControls } from './CanvasControls'
import { Canvas } from './Canvas'
import { Script } from './Script'
import { Menu } from './Menu'
import { LanguageDetectionModal } from './LanguageDetectionModal'

const useDetectLanguage = () => {
  const [isDetecting, setIsDetecting] = useState(false)

  const [detectedLanguage, setDetectedLanguage] = useState<{
    language: string
    script: string
  }>()

  /**
   * 언어 감지 함수
   *
   * @returns - 감지된 언어 정보 또는 undefined\
   * undefined인 경우는 감지하지 못 한 경우임\
   * .fromLanguage와 .detectedLanguage가 달라도 isMatchedLanguage가 true인 경우에는\
   * 통과임
   */
  const detectLanguage = useCallback(
    async ({
      fromLanguage,
      script,
    }: {
      fromLanguage: string
      script: string
    }): Promise<
      | {
          fromLanguage: string
          detectedLanguage: string
          isMatchedLanguage: boolean
          script: string
        }
      | undefined
    > => {
      // if (!BK_BACKEND_URL) {
      //   return
      // }

      if (isDetecting) {
        return
      }

      try {
        setIsDetecting(true)

        // 중복 공백 제거 후 단어 수 세기
        const trimmedScript = script.replace(/[\s]+/g, ' ').trim()
        const wordCount = trimmedScript.split(' ').length
        const charCount = trimmedScript.length

        /**
         * 검사 기준 단어, 글자 수
         */
        const MIN_LANGUAGE_DETECTION_WORD_COUNT = 3
        const MIN_LANGUAGE_DETECTION_CHAR_COUNT = 10

        /**
         * 단어 수, 글자 수가 기준치 미만이면 검사하지 않음
         */
        if (
          wordCount < MIN_LANGUAGE_DETECTION_WORD_COUNT &&
          charCount < MIN_LANGUAGE_DETECTION_CHAR_COUNT
        ) {
          return {
            fromLanguage,
            detectedLanguage: fromLanguage,
            isMatchedLanguage: true,
            script,
          }
        }

        if (!!detectedLanguage && detectedLanguage.script === trimmedScript) {
          return {
            script: trimmedScript,
            fromLanguage,
            detectedLanguage: detectedLanguage.language,
            isMatchedLanguage: isMatchLanguage(
              fromLanguage,
              detectedLanguage.language,
            ),
          }
        }

        const { data } = await axios.post<{
          success: boolean
          data: {
            lang_cd: string
            score: number
          }
        }>('/api/backend/detection', {
          apiUrl: '/api/v2/detection',
          method: 'POST',
          params: { text: trimmedScript },
        })

        let language = data.data.lang_cd.split('-')[0]

        if (isSimilarChineseLanguage(language)) {
          language = 'cmn' //이상하게도 실서버에서는 중국어를 cmn 만 준다.
        }

        setDetectedLanguage({
          language,
          script: trimmedScript,
        })

        return {
          fromLanguage,
          detectedLanguage: language,
          script,
          isMatchedLanguage: isMatchLanguage(fromLanguage, language),
        }
      } finally {
        setIsDetecting(false)
      }
    },
    [isDetecting, detectedLanguage],
  )

  return {
    /**
     * 언어 감지 함수
     *
     * @returns - 감지된 언어 정보 또는 undefined\
     * undefined인 경우는 감지하지 못 한 경우임\
     * .fromLanguage와 .detectedLanguage가 달라도 isMatchedLanguage가 true인 경우에는\
     * 통과임
     */
    detectLanguage,
    detectedLanguage,
    isDetecting,
  }
}

const DELAY = 1000 * 5
const delay = (ms = DELAY) => new Promise((resolve) => setTimeout(resolve, ms))

const useExportPreviewAudio = () => {
  const isMounted = useMountedState()

  const [isExportingPreviewAudio, setIsExportingPreviewAudio] = useState(false)
  const [error, setError] = useState<Error>()

  const [mutateExportPreviewAudio] =
    solidBackgroundVideoApi.useExportPreviewAudioMutation()

  const [getExportingPreviewAudio] =
    solidBackgroundVideoApi.useLazyGetExportingPreviewAudioQuery({
      refetchOnFocus: false,
      refetchOnReconnect: false,
    })

  const exportPreviewAudio = useCallback(
    async ({
      script,
      voice,
      tempo,
      detectedLanguage,
    }: {
      script: string
      voice: {
        language: string
        voiceId: string
        modelId: string
        gender: Gender
        isOriginalVoice: boolean
        type: string
      }
      tempo: TextScriptTempo
      detectedLanguage: string
    }) => {
      if (isExportingPreviewAudio) {
        return
      }

      try {
        setIsExportingPreviewAudio(true)
        setError(undefined)

        // 1. 보이스 생성 요청
        const {
          backendTaskId,
        } = await mutateExportPreviewAudio({
          script,
          voice,
          detectedLanguage,
          tempo,
        }).unwrap()

        if (!backendTaskId) {
          throw new Error('Failed to export preview audio')
        }

        while (true) {
          if (!isMounted()) {
            throw new Error('Unmounted')
          }

          const exportingPreviewAudio = await getExportingPreviewAudio({
            backendTaskId,
          }).unwrap()

          if (!isMounted()) {
            throw new Error('Unmounted')
          }

          /**
           * 미리듣기 생성 완료되면 리턴
           */
          if (exportingPreviewAudio.audio) {
            return {
              url: exportingPreviewAudio.audio.url,
              duration: exportingPreviewAudio.audio.duration,
            }
          }

          await delay()
        }
      } catch (e) {
        if (e instanceof Error) {
          setError(e)
        }

        const message =
          e instanceof Error
            ? e.message
            : 'An error occurred while exporting preview audio'
        setError(new Error(message))
      } finally {
        setIsExportingPreviewAudio(false)
      }
    },
    [
      isExportingPreviewAudio,
      mutateExportPreviewAudio,
      getExportingPreviewAudio,
      isMounted,
    ],
  )

  return {
    isExportingPreviewAudio,
    exportPreviewAudio,
    error,
  }
}

const useDirectDownload = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<unknown>()

  const [jobId, setJobId] = useState<string>()

  const isMounted = useMountedState()

  const [mutateExportJob] = solidBackgroundVideoApi.useExportJobMutation()

  const [getJob] = solidBackgroundVideoApi.useLazyGetJobQuery({
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  const download = useCallback(
    async ({ jobId: exportingJobId }: { jobId: string }) => {
      if (isLoading) {
        return
      }

      setIsLoading(true)
      setJobId(exportingJobId)
      setError(undefined)

      try {
        await mutateExportJob({
          jobId: exportingJobId,
        }).unwrap()

        while (true) {
          if (!isMounted()) {
            return
          }

          const job = await getJob(exportingJobId).unwrap()

          /**
           * 성공인 경우
           */
          if (
            job.generateState ===
              SOLID_BACKGROUND_VIDEO_GENERATE_STATE.EXPORTED &&
            !!job.exportedFileUrl
          ) {
            return job.exportedFileUrl
          }

          /**
           * 실패인 경우 (exporting이 아니면...)
           */
          if (
            job.generateState !==
            SOLID_BACKGROUND_VIDEO_GENERATE_STATE.EXPORTING
          ) {
            throw new Error(job.error || 'Failed to export video')
          }

          await delay()
        }
      } catch (e) {
        console.error('download error', e)
        setError(e)
      } finally {
        setIsLoading(false)
      }
    },
    [isLoading, isMounted, mutateExportJob, getJob],
  )

  const states = useMemo(
    () => ({
      isLoading,
      error,
      jobId,
    }),
    [isLoading, error, jobId],
  )

  return useMemo<[typeof download, typeof states]>(
    () => [download, states],
    [download, states],
  )
}

type SolidBackgroundVideoProps = {
  redirected?: {
    projectName: string
    voice: {
      modelId: string
      voiceId: string
      language: string
      gender: Gender
      isOriginalVoice: boolean
      type: string
    }
    orientation: Orientation
    backgroundColor: string
    modelPosition: {
      left: number
      top: number
      scaleX: number
      scaleY: number
    }

    scriptType: ScriptType

    textScriptDelta?: Partial<DeltaStatic>
    textScriptHtml?: string
    dictionary?: Record<string, string>
    tempo?: TextScriptTempo

    audioScriptName?: string
    audioScriptUrl?: string
  }
}

export const SolidBackgroundVideo: FC<SolidBackgroundVideoProps> = ({
  redirected,
}) => {
  const { t } = useTranslation('common')
  const session = useSession()

  const locale = (getCookie('NEXT_LOCALE') || 'en') as string
  const isLoggedIn = !!session.data?.user?.id
  const { accountUserInfo, workspaceList, selectedWorkspaceIndex } =
    useAppSelector((store) => getDashBoardState(store))

  const workspaceId = useMemo(() => {
    return (
      workspaceList[selectedWorkspaceIndex]?.workspaceId ||
      accountUserInfo?.workspaceId ||
      ''
    )
  }, [accountUserInfo?.workspaceId, selectedWorkspaceIndex, workspaceList])

  const { open: openNotification, contextHolder } = useToolsNotification()

  useEffect(() => {
    if (!redirected) {
      return
    }

    openNotification(t('Project generation failed. Please try again later.'))
  }, [redirected, t, openNotification])

  /**
   * 음성 리스트 등 미리 조회
   */
  const { data: modelsData } = useGetModelsQuery(
    { languageCode: locale, workspaceId },
    {
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
    },
  )

  /**
   * 기본 모델로만 필터링
   */
  const models = useMemo(() => {
    return modelsData?.filter((model) => model.id === DEFAULT_MODEL_ID)
  }, [modelsData])

  /**
   * TTS 리스트 조회
   */
  const { data: ttsListData } = useGetModelVoicesQuery(
    {},
    {
      refetchOnFocus: false,
      refetchOnMountOrArgChange: false,
      refetchOnReconnect: false,
    },
  )

  /**
   * TODO: 지금은 딥브레인과 일레븐랩스, language가 multi인 것은 제외하도록 함\
   * TODO: 추후 에디터와 동일하게 모든 TTS가 나오도록 해야 함
   */
  const ttsList = useMemo(() => {
    return ttsListData
      ?.filter((voice) => voice.type?.toLowerCase() !== 'deepbrainai')
      .filter(
        (voice) => voice.type !== 'elevenlabs' && voice.language !== 'multi',
      )
  }, [ttsListData])

  const defaultModel = useMemo(() => {
    return models?.find((model) => model.id === DEFAULT_MODEL_ID)
  }, [models])

  /**
   * 언어의 기본 음성 가져오기
   */
  const getDefaultVoiceByLanguage = useCallback(
    (
      language: string,
    ): {
      modelId: string
      voiceId: string
      language: string
      gender: Gender
      isOriginalVoice: boolean
      type: string
    } | void => {
      if (!ttsList) {
        return
      }

      const languageCode = language.split('-')[0]

      if (languageCode === 'en') {
        if (!defaultModel) {
          return
        }

        return {
          modelId: defaultModel.id,
          voiceId: `deepbrainai/${defaultModel.languageSupport[0]}/${defaultModel.id}`,
          language: languageCode,
          gender: defaultModel.tags.includes('[sex1]')
            ? GENDER.MALE
            : GENDER.FEMALE,
          isOriginalVoice: true,
          type: 'deepbrainai',
        }
      }

      const tts = ttsList.find((_tts) =>
        isSameLanguage(_tts.language, language),
      )

      if (!tts) {
        return
      }

      return {
        modelId: tts.model,
        voiceId: tts.id,
        language: tts.language,
        gender: (tts.gender?.toLocaleUpperCase() as Gender) ?? GENDER.MALE,
        isOriginalVoice: false,
        type: tts.type,
      }
    },
    [ttsList, defaultModel],
  )

  const defaultVoice = useMemo(() => {
    if (!defaultModel) {
      return
    }

    return getDefaultVoiceByLanguage(defaultModel.languageSupport[0])
  }, [defaultModel, getDefaultVoiceByLanguage])

  /**
   * 선택된 음성
   */
  const [voice, setVoice] = useState<
    | {
        modelId: string
        voiceId: string
        language: string
        gender: Gender
        isOriginalVoice: boolean
        type: string
      }
    | undefined
  >(() => {
    if (redirected) {
      return redirected.voice
    }
  })

  useEffect(() => {
    if (!!voice || !defaultVoice) {
      return
    }

    setVoice(defaultVoice)
  }, [voice, defaultVoice])

  /**
   * 나머지 로컬 스테이트...
   */
  const [projectName, setProjectName] = useState(() => {
    if (redirected) {
      return redirected.projectName
    }

    return ''
  })

  const [orientation, setOrientation] = useState(() => {
    if (redirected) {
      return redirected.orientation
    }

    return ORIENTATION.PORTRAIT
  })
  const [backgroundColor, setBackgroundColor] = useState(() => {
    if (redirected) {
      return redirected.backgroundColor
    }

    return DEFAULT_BACKGROUND_COLOR
  })

  const [modelPosition, setModelPosition] = useState<{
    left: number
    top: number
    scaleX: number
    scaleY: number
  }>(() => {
    if (redirected) {
      return redirected.modelPosition
    }

    return {
      left: 0,
      top: 0,
      scaleX: 1,
      scaleY: 1,
    }
  })

  const [scriptType, setScriptType] = useState(() => {
    if (redirected) {
      return redirected.scriptType
    }

    return SCRIPT_TYPE.TEXT
  })

  /**
   * 텍스트 스크립트 관련 상태들...
   */
  const [textScriptDelta, setTextScriptDelta] = useState(() => {
    if (redirected) {
      return redirected.textScriptDelta as DeltaStatic | undefined
    }

    return undefined
  })
  const [textScriptPlainText, setTextScriptPlainText] = useState<
    string | undefined
  >(() => {
    if (redirected) {
      return redirected.textScriptDelta?.ops
        ?.map((op) => (typeof op.insert === 'string' ? op.insert : ' '))
        .join('')
    }
  })
  const [textScriptHtml, setTextScriptHtml] = useState<string | undefined>(
    () => {
      if (redirected) {
        return redirected.textScriptHtml
      }
    },
  )
  const [tempo, setTempo] = useState<TextScriptTempo>('1.0')
  const [dictionary, setDictionary] = useState<Record<string, string>>({})

  /**
   * 오디오 스크립트 관련 상태들...
   */
  const [uploadedAudioScriptUrl, setUploadedAudioScriptUrl] = useState<
    string | undefined
  >(() => {
    if (redirected) {
      return redirected.audioScriptUrl
    }
  })
  const [audioScriptName, setAudioScriptName] = useState<string | undefined>(
    () => {
      if (redirected) {
        return redirected.audioScriptName
      }
    },
  )
  const [audioScriptFile, setAudioScriptFile] = useState<File>()

  /**
   * 입력된 텍스트 스크립트를 가공하여 오디오 추출 API 호출을 위한 텍스트로 변환
   *
   * - 사전 단어 치환
   * - 일시 정지 태그 넣기
   */
  const textScriptForGeneratePreviewAudio = useMemo(() => {
    if (!textScriptDelta) {
      return
    }

    return textScriptDelta.ops
      ?.map((op) => {
        if (op.insert && typeof op.insert === 'string') {
          return {
            ...op,
            /**
             * 사전 단어 교체
             */
            insert: Object.keys(dictionary).reduce(
              (replacedPlainText, targetWord) => {
                return replacedPlainText.replace(
                  new RegExp(targetWord, 'g'),
                  dictionary[targetWord],
                )
              },
              op.insert,
            ),
          }
        }

        if (!!op.insert?.pause) {
          return {
            ...op,
            /**
             * 일시 정지 처리
             *
             * 기존 에디터에서 사용하던 포맷과 동일하게 처리
             */
            insert: `<div class="partialOpacity pauseButton" data-wordtype="pause">${op.insert.pause.duration}</div>`,
          }
        }

        return {
          ...op,
          insert: '',
        }
      })
      .map((op) => op.insert)
      .join('')
  }, [textScriptDelta, dictionary])

  /**
   * 언어 감지 훅
   */
  const {
    detectLanguage,
    detectedLanguage,
    isDetecting: isDetectingLanguage,
  } = useDetectLanguage()

  /**
   * 다운로드 가능한지 여부
   */
  const canDownload = useMemo(() => {
    const canDownloadCommon = !!voice

    if (!canDownloadCommon) {
      return false
    }

    const canDownloadTextScript = (textScriptPlainText?.trim().length ?? 0) > 0

    const canDownloadAudioScript = !!uploadedAudioScriptUrl || !!audioScriptFile

    if (scriptType === SCRIPT_TYPE.TEXT) {
      return canDownloadTextScript
    } else if (scriptType === SCRIPT_TYPE.AUDIO) {
      return canDownloadAudioScript
    }

    return false
  }, [
    voice,
    textScriptPlainText,
    uploadedAudioScriptUrl,
    audioScriptFile,
    scriptType,
  ])

  /**
   * 보이스, 잡 생성 관련 API
   */
  const {
    exportPreviewAudio,
    isExportingPreviewAudio,
    error: errorExportPreviewAudio,
  } = useExportPreviewAudio()

  const [
    mutateCreateJob,
    { isLoading: isMutatingCreateJob, error: errorCreateJob },
  ] = solidBackgroundVideoApi.useCreateJobMutation()

  const [download, { isLoading: isDownloading, error: errorDownload }] =
    useDirectDownload()

  useEffect(() => {
    if (!errorExportPreviewAudio) {
      return
    }

    openNotification(t('Audio synthesis failed. Please try again later.'))
  }, [errorExportPreviewAudio, openNotification, t])

  useEffect(() => {
    if (!errorCreateJob) {
      return
    }

    openNotification(t('Project generation failed. Please try again later.'))
  }, [errorCreateJob, openNotification, t])

  useEffect(() => {
    if (!errorDownload) {
      return
    }

    openNotification(t('Download failed. Please try again.'))
  }, [errorDownload, openNotification, t])

  const [generatedPreviewAudio, setGeneratedPreviewAudio] = useState<{
    url: string
    textScript: string
    voiceId: string
    tempo: TextScriptTempo
  }>()

  /**
   * 언어 감지 모달 관련
   */
  const [isOpenedLanguageDetectionModal, setIsOpenedLanguageDetectionModal] =
    useState(false)

  const handleConfirmLanguageDetection = useCallback(() => {
    setIsOpenedLanguageDetectionModal(false)

    if (!detectedLanguage) {
      return
    }

    const matchedVoiceByDetectedLanguage = getDefaultVoiceByLanguage(
      detectedLanguage.language,
    )

    if (!matchedVoiceByDetectedLanguage) {
      return alert(
        t(
          'Voice tts corresponding to the entered script language is not provided. Please contact the customer service center.',
        ),
      )
    }

    setVoice(matchedVoiceByDetectedLanguage)
  }, [t, getDefaultVoiceByLanguage, detectedLanguage, setVoice])

  /**
   * 언어 감지 후 모달 열기
   */
  const detectLanguageAndOpenModal = useCallback(async () => {
    if (!voice) {
      return
    }

    const resultDetectLanguage = await detectLanguage({
      fromLanguage: voice.language,
      script: textScriptPlainText || '',
    }).catch((e) => {
      console.error(e)

      openNotification(t('Language detection failed. Please try again later.'))
    })

    /**
     * 언어 감지 실패 시에는 중단
     */
    if (!resultDetectLanguage) {
      return
    }

    if (!resultDetectLanguage.isMatchedLanguage) {
      setIsOpenedLanguageDetectionModal(true)
      return
    }
  }, [
    textScriptPlainText,
    voice,
    detectLanguage,
    setIsOpenedLanguageDetectionModal,
    openNotification,
    t,
  ])

  const detectLanguageAndOpenModalRef = useRef(detectLanguageAndOpenModal)

  useEffect(() => {
    detectLanguageAndOpenModalRef.current = detectLanguageAndOpenModal
  }, [detectLanguageAndOpenModal])

  /**
   * 붙여넣기 시 언어 감지\
   * 언어 감지 함수를 ref에 넣고, 실행 전 딜레이를 준 이유는...\
   * 스테이트(스크립트)가 변경된 이후 언어 감지를 실행하기 위함임
   */
  const handlePasteScript = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve))
    await detectLanguageAndOpenModalRef.current()
  }, [detectLanguageAndOpenModalRef])

  const isChangedAfterGeneratePreviewAudio = useMemo(() => {
    if (!generatedPreviewAudio) {
      return true
    }

    return (
      generatedPreviewAudio.textScript !== textScriptForGeneratePreviewAudio ||
      generatedPreviewAudio.voiceId !== voice?.voiceId ||
      generatedPreviewAudio.tempo !== tempo
    )
  }, [generatedPreviewAudio, textScriptForGeneratePreviewAudio, voice, tempo])

  /**
   * 보이스 생성
   */
  const generatePreviewAudio = useCallback(
    async (_voice: {
      modelId: string
      voiceId: string
      language: string
      gender: Gender
      isOriginalVoice: boolean
      type: string
    }) => {
      if (!textScriptPlainText?.trim()) {
        return
      }

      if (!textScriptForGeneratePreviewAudio) {
        return
      }

      if (isExportingPreviewAudio) {
        return
      }

      if (!_voice) {
        return
      }

      const resultDetectLanguage = await detectLanguage({
        fromLanguage: _voice?.language,
        script: textScriptPlainText?.trim() || '',
      }).catch((e) => {
        console.error(e)

        openNotification(
          t('Language detection failed. Please try again later.'),
        )
      })

      /**
       * 언어 감지 실패 시에는 중단
       */
      if (!resultDetectLanguage) {
        return
      }

      if (!resultDetectLanguage.isMatchedLanguage) {
        setIsOpenedLanguageDetectionModal(true)
        return
      }

      const result = await exportPreviewAudio({
        script: textScriptForGeneratePreviewAudio,
        voice: _voice,
        detectedLanguage: resultDetectLanguage.detectedLanguage,
        tempo,
      })

      if (!result) {
        return
      }

      setGeneratedPreviewAudio({
        url: result.url,
        textScript: textScriptForGeneratePreviewAudio,
        voiceId: _voice.voiceId,
        tempo: tempo,
      })
    },
    [
      openNotification,
      t,
      textScriptPlainText,
      textScriptForGeneratePreviewAudio,
      isExportingPreviewAudio,
      tempo,
      detectLanguage,
      exportPreviewAudio,
    ],
  )

  const handleGeneratePreviewAudio = useCallback(() => {
    if (!voice) {
      return
    }

    generatePreviewAudio(voice)
  }, [generatePreviewAudio, voice])

  const createJob = useCallback(async () => {
    if (!voice) {
      throw new Error('Voice is required')
    }

    if (isMutatingCreateJob) {
      throw new Error('Creating job is in progress')
    }

    let params: Parameters<typeof mutateCreateJob>[0] = {
      projectName: projectName || DEFAULT_PROJECT_NAME,
      orientation,
      backgroundColor,
      voice,
      modelPosition,
      scriptType,
    }

    if (scriptType === SCRIPT_TYPE.TEXT) {
      const resultDetectLanguage = await detectLanguage({
        fromLanguage: voice.language,
        script: textScriptPlainText?.trim() || '',
      }).catch((e) => {
        console.error(e)

        openNotification(
          t('Language detection failed. Please try again later.'),
        )
      })

      /**
       * 언어 감지 실패 시에는 중단
       */
      if (!resultDetectLanguage) {
        throw new Error('Language detection failed')
      }

      if (!resultDetectLanguage.isMatchedLanguage) {
        setIsOpenedLanguageDetectionModal(true)
        throw new Error('Language is not matched')
      }

      params = {
        ...params,
        textScriptHtml,
        textScriptDelta,
        tempo,
        dictionary,
      }
    }

    if (scriptType === SCRIPT_TYPE.AUDIO) {
      params = {
        ...params,
        audioScriptUrl: uploadedAudioScriptUrl,
        audioScriptName,
        audioScriptFile,
      }
    }

    const { jobId } = await mutateCreateJob(params).unwrap()

    return jobId
  }, [
    projectName,
    orientation,
    backgroundColor,
    voice,
    modelPosition,
    scriptType,
    textScriptHtml,
    textScriptDelta,
    tempo,
    dictionary,
    uploadedAudioScriptUrl,
    audioScriptName,
    audioScriptFile,
    isMutatingCreateJob,
    detectLanguage,
    openNotification,
    t,
    textScriptPlainText,
    mutateCreateJob,
  ])

  /**
   * 프로젝트로 내보내기
   */
  const handleExportProject = useCallback(async () => {
    try {
      const jobId = await createJob()

      /**
       * router를 사용하지 않고 location.href를 사용하는 이유
       *
       * 콜백 페이지로 이동 중 오류 발생 시 현재 페이지로 되돌아 오도록 되어 있음\
       * 이 경우 현재 컴포넌트(페이지)가 언마운트/마운트 되지 않고 프로퍼티만 변경됨\
       * 현재 기능 구현 특성 상 초기 데이터를 받아와 처리해야 하는 부분들이 있음\
       * 초기 데이터를 받아오도록 할 때 최초 한 번만 실행하도록 구현했음\
       * 그렇기 때문에 에러 후 언마운트/마운트 되지 않으면 초기 데이터 관련 동작을 하지 않음\
       * 대응 방법은 초기 데이터를 받아오는 부분을 중간에 초기 데이터가 바뀌더라도\
       * 동작하도록 구현을 변경하는 방법도 있지만\
       * 이번에는 간단하게 location.href를 사용하여 강제로 언마운트/마운트 되도록 하고\
       * 기존 구현은 그대로 유지하는 방향으로 함
       */
      window.location.href = `/tools/solid-background-video/callback/create-project?jobId=${jobId}`
    } catch (e) {
      console.error(e)
      // do nothing
    }
  }, [createJob])

  /**
   * 비디오로 다운로드하기
   */
  const handleDownloadVideo = useCallback(async () => {
    try {
      const jobId = await createJob()

      /**
       * 로그인 되어 있으면 바로 다운로드
       */
      if (isLoggedIn) {
        const downloadUrl = await download({ jobId })

        if (!downloadUrl) {
          return
        }

        const a = document.createElement('a')
        a.href = downloadUrl
        a.download = 'SolidBackgroundVideo.mp4'
        a.click()
        a.remove()
      } else {
        /**
         * 로그인 되어 있지 않으면 다운로드 페이지로 이동
         */
        /**
         * router를 사용하지 않고 location.href를 사용하는 이유
         *
         * 콜백 페이지로 이동 중 오류 발생 시 현재 페이지로 되돌아 오도록 되어 있음\
         * 이 경우 현재 컴포넌트(페이지)가 언마운트/마운트 되지 않고 프로퍼티만 변경됨\
         * 현재 기능 구현 특성 상 초기 데이터를 받아와 처리해야 하는 부분들이 있음\
         * 초기 데이터를 받아오도록 할 때 최초 한 번만 실행하도록 구현했음\
         * 그렇기 때문에 에러 후 언마운트/마운트 되지 않으면 초기 데이터 관련 동작을 하지 않음\
         * 대응 방법은 초기 데이터를 받아오는 부분을 중간에 초기 데이터가 바뀌더라도\
         * 동작하도록 구현을 변경하는 방법도 있지만\
         * 이번에는 간단하게 location.href를 사용하여 강제로 언마운트/마운트 되도록 하고\
         * 기존 구현은 그대로 유지하는 방향으로 함
         */
        window.location.href = `/tools/solid-background-video/callback/direct-download?jobId=${jobId}`
      }
    } catch (e) {
      console.error(e)
      // do nothing
    }
  }, [createJob, isLoggedIn, download])

  return (
    <StyledCustomMenuToolsLayout
      menu={
        <Menu
          projectName={projectName}
          onChangeProjectName={setProjectName}
          onGoToAdvancedEditor={handleExportProject}
          onDownload={handleDownloadVideo}
          disabledDownload={!canDownload}
          isLoading={isMutatingCreateJob}
          isDownloading={isDownloading}
        />
      }
      fillMainHeight
    >
      <SolidBackgroundVideoContainer>
        <LeftContainer>
          {/* 
            배경색, 캔버스 방향 변경 기능은 제공하지 않음
          */}
          {/* <CanvasControlsContainer>
            <CanvasControls
              orientation={orientation}
              onChangeOrientation={setOrientation}
              backgroundColor={backgroundColor}
              onChangeBackgroundColor={setBackgroundColor}
            />
          </CanvasControlsContainer> */}

          <CanvasContainer>
            <Canvas
              backgroundColor={backgroundColor}
              orientation={orientation}
              model={defaultModel}
              modelPosition={modelPosition}
              onChangeModelPosition={setModelPosition}
            />
          </CanvasContainer>

          <AudioPlayerContainer>
            <AudioPlayer
              src={generatedPreviewAudio?.url}
              isLoading={isExportingPreviewAudio}
              disabled={
                scriptType !== SCRIPT_TYPE.TEXT ||
                isExportingPreviewAudio ||
                !generatedPreviewAudio?.url
              }
            />
          </AudioPlayerContainer>
        </LeftContainer>

        <RightContainer>
          <Script
            scriptType={scriptType}
            onChangeScriptType={setScriptType}
            textScript={textScriptDelta}
            onChangeTextScript={({ html, delta, plainText }) => {
              setTextScriptHtml(html)
              setTextScriptDelta(delta)
              setTextScriptPlainText(plainText)
            }}
            onPasteTextScript={handlePasteScript}
            tempo={tempo}
            onChangeTempo={setTempo}
            dictionary={dictionary}
            onChangeDictionary={setDictionary}
            audioScriptUrl={uploadedAudioScriptUrl}
            audioScriptFile={audioScriptFile}
            onChangeAudioScriptFile={(_audioScriptFile) => {
              setUploadedAudioScriptUrl(undefined)
              setAudioScriptName(_audioScriptFile?.name)
              setAudioScriptFile(_audioScriptFile)
            }}
            voice={voice}
            onChangeVoice={setVoice}
            models={models}
            ttsList={ttsList}
            onGeneratePreviewAudio={handleGeneratePreviewAudio}
            disabledGeneratePreviewAudio={
              textScriptPlainText?.trim().length === 0 ||
              !isChangedAfterGeneratePreviewAudio
            }
            isGeneratingPreviewAudio={
              isDetectingLanguage || isExportingPreviewAudio
            }
          />
        </RightContainer>
      </SolidBackgroundVideoContainer>

      {detectedLanguage && (
        <LanguageDetectionModal
          isOpened={isOpenedLanguageDetectionModal}
          onClose={() => setIsOpenedLanguageDetectionModal(false)}
          onConfirm={handleConfirmLanguageDetection}
          fromLanguage={voice?.language}
          detectedLanguage={detectedLanguage.language}
        />
      )}

      {contextHolder}
    </StyledCustomMenuToolsLayout>
  )
}

const StyledCustomMenuToolsLayout = styled(CustomMenuToolsLayout)`
  & > main {
    background-color: #f5f5f5;
  }
`

const SolidBackgroundVideoContainer = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  height: 100%;
  padding: 15px;
  box-sizing: border-box;

  width: calc(100vw - 30px - 24px);
  min-width: 1280px;
  max-width: 1920px;
  min-height: 720px;
  max-height: 1080px;

  display: flex;
  gap: 15px;

  margin: auto;
  inset: 0;
  position: relative;
`

const LeftContainer = styled.div`
  width: calc(100% - 500px - 15px);

  display: flex;
  flex-direction: column;
  gap: 15px;
`

const CanvasControlsContainer = styled.div`
  flex: 0;
`

const CanvasContainer = styled.div`
  flex: 1;

  overflow: hidden;
`

const AudioPlayerContainer = styled.div`
  flex: 0;
`

const RightContainer = styled.div`
  width: 500px;
`
